import { z } from "zod";
import { BusinessCategorySchema, BusinessPlanSchema, BusinessAddressSchema, SlotDTOSchema, } from "./pro.js";
// ─────────────────────────────────────────────────────────────────────────────
// Piste V10 — Comptes Professionnels, PHASES 3-4 (docs/PRO_ACCOUNTS_SPEC.md)
// Monétisation (EPIC 10), Dev Center (EPIC 14), interop calendrier niveau 2
// (EPIC 9), recherche publique / IA-ready (EPIC 13).
//
// GATE (AGENTS.md §10) : les DTO/inputs de monétisation ne sont exploitables
// que si MONETIZATION_ENABLED / PREPAY_ENABLED sont true côté serveur. Ces
// schémas décrivent la frontière ; c'est la route qui refuse quand le flag est
// OFF (403/409). Aucun schéma ne force un paiement par lui-même.
//
// RÈGLE US-125/US-132 : aucun DTO exposé (recherche publique, webhooks tiers)
// ne contient l'email ni le téléphone d'un client — jamais de donnée client.
// ─────────────────────────────────────────────────────────────────────────────
// ── Enums (miroir des enums Prisma) ──────────────────────────────────────────
export const SubscriptionStatusSchema = z.enum([
    "NONE",
    "ACTIVE",
    "PAST_DUE",
    "CANCELED",
]);
export const PaymentKindSchema = z.enum(["PREPAY", "SUBSCRIPTION"]);
export const PaymentStatusSchema = z.enum([
    "PENDING",
    "SUCCEEDED",
    "REFUNDED",
    "FAILED",
]);
export const CalProviderSchema = z.enum(["GOOGLE", "MICROSOFT", "CALDAV"]);
// ── Monétisation : abonnement PRO (US-101) ───────────────────────────────────
/**
 * État d'abonnement d'un business. `isPro` est DÉRIVÉ (status === ACTIVE) pour
 * l'UI : tant que la monétisation est OFF, le status reste NONE et `isPro` est
 * false — mais toutes les features restent accessibles en FREE (le pro est
 * gratuit, cf. AGENTS.md §10).
 */
export const ProSubscriptionDTOSchema = z.object({
    businessId: z.string().uuid(),
    plan: BusinessPlanSchema,
    status: SubscriptionStatusSchema,
    currentPeriodEnd: z.string().datetime().nullable(),
    /** Dérivé serveur : status === "ACTIVE". Ne jamais afficher de tarif. */
    isPro: z.boolean(),
});
/**
 * Ouverture d'une session de checkout Stripe Billing (US-101). GATÉ : la route
 * renvoie 403 tant que MONETIZATION_ENABLED est false. Les URLs de retour sont
 * contrôlées côté serveur (jamais une URL arbitraire du client).
 */
export const CreateCheckoutInputSchema = z.object({
    /** Cible d'abonnement — seul PRO est achetable (FREE = pas de checkout). */
    plan: z.literal("PRO"),
});
// ── Paiements & remboursements (US-102/103/104) ──────────────────────────────
/** Paiement historisé (prépaiement d'une résa ou abonnement). */
export const PaymentDTOSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid().nullable(),
    bookingId: z.string().uuid().nullable(),
    clientId: z.string().uuid().nullable(),
    amountCents: z.number().int().nonnegative(),
    currency: z.string(),
    kind: PaymentKindSchema,
    status: PaymentStatusSchema,
    createdAt: z.string().datetime(),
});
/**
 * Déclenchement d'un remboursement (US-104). `amountCents` absent = total ;
 * sinon partiel (borné côté serveur au montant du paiement et aux paliers de
 * BookingPolicy). GATÉ derrière PREPAY_ENABLED.
 */
export const RefundInputSchema = z.object({
    paymentId: z.string().uuid(),
    amountCents: z.number().int().positive().optional(),
    reason: z.string().trim().max(500).optional(),
});
/**
 * Préparation d'un prépaiement de résa (US-102/103). GATÉ derrière
 * PREPAY_ENABLED : la route renvoie 503 tant que le flag est false. Le montant
 * n'est jamais fixé par le client — le serveur le dérive du prix de la
 * prestation liée au booking (point de branchement futur documenté). On ne
 * transmet ici que la référence du booking à prépayer.
 */
export const CreatePaymentIntentInputSchema = z.object({
    bookingId: z.string().uuid(),
});
// ── RDV récurrents (US-155, GATÉ RECURRING_BOOKINGS_ENABLED) ─────────────────
/**
 * Création d'une série « tous les mardis 18h » (US-155). Le business est
 * dérivé du handle côté serveur ; startAt = première occurrence (l'heure
 * murale Europe/Paris est préservée à travers les DST).
 */
export const CreateRecurringBookingInputSchema = z.object({
    workerId: z.string().uuid(),
    serviceId: z.string().uuid(),
    startAt: z.string().datetime(),
    /** Cadence en semaines (1 = hebdo, 2 = quinzaine…). */
    intervalWeeks: z.number().int().min(1).max(8).default(1),
});
/** Série côté client (« Mes RDV » → récurrences). */
export const BookingSeriesDTOSchema = z.object({
    id: z.string().uuid(),
    serviceId: z.string().uuid(),
    serviceName: z.string(),
    businessDisplayName: z.string(),
    businessHandle: z.string().nullable(),
    firstStartAt: z.string().datetime(),
    intervalWeeks: z.number().int().positive(),
    active: z.boolean(),
    /** Prochaine occurrence encore vivante (null si plus aucune). */
    nextOccurrenceAt: z.string().datetime().nullable(),
    upcomingCount: z.number().int().nonnegative(),
});
// ── Dev Center : apps tierces, OAuth, webhooks (EPIC 14) ─────────────────────
/** Scopes OAuth exposés aux apps tierces (US-142/147). Lecture/écriture bornées. */
export const OAuthScopeSchema = z.enum([
    "bookings:read",
    "bookings:write",
    "calendar:read",
    "profile:read",
    "crm:contacts:read",
    "crm:bookings:read",
]);
/**
 * App développeur (US-141). Le secret n'est JAMAIS renvoyé (seulement à la
 * création, une fois) : le DTO ne porte que des métadonnées publiques.
 */
export const DeveloperAppDTOSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    clientId: z.string(),
    redirectUris: z.array(z.string().url()),
    scopes: z.array(OAuthScopeSchema),
    reviewed: z.boolean(),
    createdAt: z.string().datetime(),
});
/** Création d'une app développeur (US-141). */
export const CreateAppInputSchema = z.object({
    name: z.string().trim().min(2).max(100),
    redirectUris: z.array(z.string().url()).min(1).max(10),
    scopes: z.array(OAuthScopeSchema).min(1),
});
/**
 * Autorisation OAuth d'un pro sur une app (US-142/145). Vue côté pro dans ses
 * réglages « apps connectées ». Les jetons (hachés) ne sont jamais exposés.
 */
export const OAuthGrantDTOSchema = z.object({
    id: z.string().uuid(),
    appId: z.string().uuid(),
    /** Nom de l'app, joint côté serveur pour l'affichage (US-145). */
    appName: z.string(),
    businessId: z.string().uuid(),
    scopes: z.array(OAuthScopeSchema),
    expiresAt: z.string().datetime(),
    revokedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
/** Événements webhook livrables (US-143/147). */
export const WebhookEventSchema = z.enum([
    "booking.created",
    "booking.confirmed",
    "booking.cancelled",
    "booking.completed",
    "booking.no_show",
    "message.received",
    "follower.new",
    "loyalty.stamp",
]);
/** Enregistrement d'un endpoint webhook (US-143). */
export const WebhookEndpointInputSchema = z.object({
    url: z.string().url(),
    events: z.array(WebhookEventSchema).min(1),
    active: z.boolean().default(true),
});
/** Endpoint webhook — le `secret` de signature n'est pas exposé après création. */
export const WebhookEndpointDTOSchema = z.object({
    id: z.string().uuid(),
    appId: z.string().uuid(),
    url: z.string().url(),
    events: z.array(WebhookEventSchema),
    active: z.boolean(),
    createdAt: z.string().datetime(),
});
// ── Interop calendrier niveau 2 (EPIC 9, US-92/93) ───────────────────────────
/**
 * Connexion calendrier externe. Les jetons (chiffrés au repos) ne sont JAMAIS
 * renvoyés au client : le DTO ne porte que l'état de la connexion.
 */
export const CalendarConnectionDTOSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    workerId: z.string().uuid().nullable(),
    provider: CalProviderSchema,
    externalCalendarId: z.string().nullable(),
    syncEnabled: z.boolean(),
    lastSyncAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
/**
 * Démarrage d'une connexion OAuth (Google/Microsoft). GATÉ : refusé tant que
 * les credentials OAuth ne sont pas branchés côté serveur.
 */
export const ConnectCalendarInputSchema = z.object({
    provider: z.enum(["GOOGLE", "MICROSOFT"]),
    /** Rattacher à un worker précis (agenda perso) ; sinon au business entier. */
    workerId: z.string().uuid().optional(),
});
/** Connexion CalDAV (iCloud / auto-hébergé) par mot de passe d'application. */
export const ConnectCalDavInputSchema = z.object({
    caldavUrl: z.string().url(),
    workerId: z.string().uuid().optional(),
});
/** Bascule de la synchronisation d'une connexion existante. */
export const UpdateCalendarConnectionInputSchema = z.object({
    syncEnabled: z.boolean(),
});
// ── Recherche publique & IA-ready (EPIC 13, US-131/132) ──────────────────────
/**
 * Requête de recherche publique : catégorie + zone géo + fenêtre horaire.
 * `near` est soit des coordonnées, soit une ville. C'est la MÊME requête in-app
 * et via l'API publique / MCP (US-131/132/133).
 */
export const PublicSearchQuerySchema = z
    .object({
    category: BusinessCategorySchema.optional(),
    /** Localisation : coordonnées OU ville (au moins un des deux si fourni). */
    near: z
        .object({
        lat: z.number().min(-90).max(90).optional(),
        lng: z.number().min(-180).max(180).optional(),
        city: z.string().trim().min(1).max(120).optional(),
    })
        .refine((n) => (n.lat != null && n.lng != null) || (n.city != null && n.city !== ""), { message: "near requires lat+lng or city" })
        .optional(),
    /** Fenêtre horaire demandée (« demain 14h » → from/to). */
    when: z
        .object({
        from: z.string().datetime(),
        to: z.string().datetime(),
    })
        .refine((w) => new Date(w.to).getTime() > new Date(w.from).getTime(), {
        message: "when.to must be after when.from",
        path: ["to"],
    })
        .optional(),
    /** Rayon de recherche autour de `near` (km). */
    radiusKm: z.number().positive().max(200).default(10),
})
    .refine((q) => q.near?.lat == null || (q.near.lat != null && q.near.lng != null), {
    message: "lat and lng must be provided together",
    path: ["near", "lng"],
});
/**
 * Résultat de recherche publique (US-131/132) : profil pro PUBLIC + premier
 * créneau libre dans la fenêtre. AUCUNE donnée client. Sous-ensemble strict du
 * profil pro adapté à l'exposition tierce (pas de SIRET, pas de plan interne).
 */
export const PublicSearchResultDTOSchema = z.object({
    handle: z.string(),
    displayName: z.string(),
    legalName: z.string(),
    category: BusinessCategorySchema,
    address: BusinessAddressSchema,
    bio: z.string().nullable(),
    logoKey: z.string().nullable(),
    googlePlaceId: z.string().nullable(),
    /** Distance au point `near` (km), null si recherche non géolocalisée. */
    distanceKm: z.number().nonnegative().nullable(),
    /** Premier créneau libre dans la fenêtre demandée (null si aucun). */
    firstSlot: SlotDTOSchema.nullable(),
});
// ── Ajout (agent Dev Center, E9/E14) : CalDAV avec mot de passe d'application ─
// `ConnectCalDavInputSchema` ne portait que l'URL ; la connexion CalDAV réelle
// (iCloud / auto-hébergé) exige un mot de passe d'application, chiffré au repos
// côté serveur (services/calendarSync.ts, jamais renvoyé). Schéma additionnel
// pour ne pas modifier le schéma existant.
export const ConnectCalDavWithSecretInputSchema = z.object({
    caldavUrl: z.string().url(),
    /** Mot de passe d'application CalDAV — chiffré au repos, jamais renvoyé. */
    appPassword: z.string().min(1).max(500),
    /** Identifiant/login CalDAV (souvent l'adresse iCloud). */
    username: z.string().trim().min(1).max(320),
    /** Rattacher à un worker précis (agenda perso) ; sinon au business entier. */
    workerId: z.string().uuid().optional(),
});
//# sourceMappingURL=pro3.js.map